// node_modules
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  format,
  parse,
  addHours,
  compareAsc,
  isAfter,
  isBefore,
  addDays,
  startOfWeek as startOfWeekFn,
  isSameDay,
} from "date-fns";
import FullCalendar from "@fullcalendar/react";
import { Dialog } from "primereact/dialog";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import { useDispatch, useSelector } from "react-redux";
import { useMobile } from "../../../hooks/useMobile";
import { useReactToPrint } from "react-to-print";

// redux
import {
  useGetScheduleListQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
} from "../../../redux/services/schedule";
import { useGetStationListQuery } from "../../../redux/services/foodStation";

// primereact components
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

// context
import { ToastContext } from "../../../App";
import { setLoading, setUnsavedChanges } from "../../../redux/slices/app.slice";

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./Scheduling.module.css";

const Scheduling = () => {
  const showToast = useContext(ToastContext);
  const dispatch = useDispatch();
  const { isMobile } = useMobile();
  const draggingElRef = useRef(null);
  const calendarRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(Date.now());
  const unsavedChanges = useSelector((state) => state.app.unsavedChanges);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [tempStation, setTempStation] = useState(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const {
    data: scheduling,
    isSuccess: isGetSchedulingSuccess,
    isError: isGetSchedulingError,
  } = useGetScheduleListQuery(getSubdomain());
  const [
    createSchedule,
    {
      isSuccess: isCreateScheduleSuccess,
      isError: isCreateScheduleError,
      error: createScheduleError,
    },
  ] = useCreateScheduleMutation();
  const [
    updateSchedule,
    {
      isSuccess: isUpdateScheduleSuccess,
      isError: isUpdateScheduleError,
      error: updateScheduleError,
    },
  ] = useUpdateScheduleMutation();
  const {
    data: stations,
    isSuccess: isGetStationsSuccess,
    isError: isGetStationsError,
  } = useGetStationListQuery(getSubdomain());

  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [isDragging, setIsDragging] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);

  // Flat option list: regular stations are plain items; multi-stations inject a
  // non-selectable header followed by their substations as indented items.
  const stationDropdownOptions = useMemo(() => {
    if (!stations) return [];
    const sorted = [...stations].sort((a, b) => a.name.localeCompare(b.name));
    const options = [];
    sorted.forEach((station) => {
      if (station.is_multi) {
        options.push({
          id: station.id,
          name: station.name,
          _subCount: station.sub_stations.length,
          _isGroupHeader: true,
        });
        station.sub_stations.forEach((sub) => {
          options.push({
            ...sub,
            _isSubstation: true,
            _parentId: station.id,
            _parentName: station.name,
          });
        });
      } else {
        options.push({ ...station, _isSubstation: false });
      }
    });
    return options;
  }, [stations]);
  // Grouped structure for the schedule table modal
  const groupedScheduleItems = useMemo(() => {
    if (!stations || !scheduling) return [];
    const sorted = [...stations].sort((a, b) => a.name.localeCompare(b.name));
    return sorted.map((station) => {
      if (station.is_multi) {
        return {
          id: station.id,
          label: station.name,
          subCount: station.sub_stations.length,
          isMulti: true,
          active: station.active,
          substations: station.sub_stations.map((sub) => ({
            id: sub.id,
            name: sub.name,
            schedule:
              scheduling.find((s) => s.sub_station_id === sub.id) ?? null,
          })),
        };
      }
      return {
        id: station.id,
        label: station.name,
        active: station.active,
        isMulti: false,
        schedule:
          scheduling.find(
            (s) => s.food_station_id === station.id && !s.sub_station_id,
          ) ?? null,
      };
    });
  }, [stations, scheduling]);

  const scheduleFilterOptions = [
    { label: "Enabled and Not Enabled", value: "all" },
    { label: "Enabled Only", value: "enabled" },
    { label: "Not Enabled Only", value: "not_enabled" },
  ];

  const filteredGroupedScheduleItems = useMemo(() => {
    if (scheduleFilter === "all") return groupedScheduleItems;
    return groupedScheduleItems.filter((item) => {
      if (scheduleFilter === "enabled") return item.active === 1;
      if (scheduleFilter === "not_enabled") return item.active === 0;
      return true;
    });
  }, [groupedScheduleItems, scheduleFilter]);

  const [slots, setSlots] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });

  useEffect(() => {
    if (isCreateScheduleSuccess) {
      showToast("success", "Schedule", "Schedule created successfully");
      setIsDirty(false);
      dispatch(setUnsavedChanges(false));
    }
  }, [isCreateScheduleSuccess]);

  useEffect(() => {
    if (isUpdateScheduleSuccess) {
      showToast("success", "Schedule", "Schedule updated successfully");
      setIsDirty(false);
      dispatch(setUnsavedChanges(false));
    }
  }, [isUpdateScheduleSuccess]);

  useEffect(() => {
    if (isGetSchedulingSuccess) dispatch(setLoading(false));
  }, [isGetSchedulingSuccess]);

  useEffect(() => {
    if (isGetSchedulingError) {
      showToast("error", "Schedule", "Unable to load schedule data");
      dispatch(setLoading(false));
    }
  }, [isGetSchedulingError]);

  useEffect(() => {
    if (isCreateScheduleError)
      showToast(
        "error",
        createScheduleError?.status,
        createScheduleError?.data?.message ||
          (createScheduleError?.data?.errors && "Incorrect data"),
      );
  }, [isCreateScheduleError]);

  useEffect(() => {
    if (isUpdateScheduleError)
      showToast(
        "error",
        updateScheduleError?.status,
        updateScheduleError?.data?.message ||
          (updateScheduleError?.data?.errors && "Incorrect data"),
      );
  }, [isUpdateScheduleError]);

  useEffect(() => {
    if (isGetStationsError) {
      showToast("error", "Station", "Unable to load stations");
      dispatch(setLoading(false));
    }
  }, [isGetStationsError]);

  const changeStation = (newStation) => {
    if (unsavedChanges) {
      setShowAlertDialog(true);
      setTempStation(newStation);
    } else setSelectedStation(newStation);
  };

  const confirmChangeStation = () => {
    setSelectedStation(tempStation);
    setTempStation(null);
    dispatch(setUnsavedChanges(false));
    setShowAlertDialog(false);
  };

  useEffect(() => {
    if (isGetSchedulingSuccess && selectedStation) {
      let draggableEl = document.getElementById("mydraggable");
      new Draggable(draggableEl);
      const calendarSlots = {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      };

      const schedule = selectedStation._isSubstation
        ? scheduling.find((item) => item.sub_station_id === selectedStation.id)
        : scheduling.find(
            (item) =>
              item.food_station_id === selectedStation.id &&
              !item.sub_station_id,
          );

      if (schedule) {
        schedule.days.forEach((item) => {
          switch (item.day) {
            case "sunday":
              item.slots.forEach((slot, index) => {
                calendarSlots.sunday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [0],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [0] },
                });
              });
              break;
            case "monday":
              item.slots.forEach((slot, index) => {
                calendarSlots.monday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [1],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [1] },
                });
              });
              break;
            case "tuesday":
              item.slots.forEach((slot, index) => {
                calendarSlots.tuesday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [2],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [2] },
                });
              });
              break;
            case "wednesday":
              item.slots.forEach((slot, index) => {
                calendarSlots.wednesday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [3],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [3] },
                });
              });
              break;
            case "thursday":
              item.slots.forEach((slot, index) => {
                calendarSlots.thursday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [4],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [4] },
                });
              });
              break;
            case "friday":
              item.slots.forEach((slot, index) => {
                calendarSlots.friday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [5],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [5] },
                });
              });
              break;
            case "saturday":
              item.slots.forEach((slot, index) => {
                calendarSlots.saturday.push({
                  title: `Slot ${index + 1}`,
                  daysOfWeek: [6],
                  startTime: slot.start,
                  endTime: slot.end,
                  constraint: { daysOfWeek: [6] },
                });
              });
              break;
          }
        });
      }

      setSlots(calendarSlots);
      setIsDirty(false);
      dispatch(setUnsavedChanges(false));
    }
  }, [selectedStation, isGetSchedulingSuccess]);

  const saveSchedule = () => {
    const schedule = selectedStation._isSubstation
      ? scheduling.find((item) => item.sub_station_id === selectedStation.id)
      : scheduling.find(
          (item) =>
            item.food_station_id === selectedStation.id && !item.sub_station_id,
        );

    const payload = {
      days: [],
    };

    Object.keys(slots).forEach((day) => {
      const oneDay = { day, slots: [] };
      slots[day].forEach((slot) => {
        oneDay.slots.push({ start: slot.startTime, end: slot.endTime });
      });
      if (oneDay.slots.length) {
        payload.days.push(oneDay);
      }
    });

    if (schedule) {
      updateSchedule({
        id: schedule.id,
        payload,
        subDomain: getSubdomain(),
      });
    } else {
      if (selectedStation._isSubstation) {
        payload.food_station_id = selectedStation._parentId;
        payload.sub_station_id = selectedStation.id;
      } else {
        payload.food_station_id = selectedStation.id;
      }
      payload.subdomain = getSubdomain();
      createSchedule(payload);
    }
  };

  const weekDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const normalizeSlots = (stationDays) => {
    const slotsByDay = {};

    weekDays.forEach((day) => {
      const dayData = stationDays.find((d) => d.day === day);
      slotsByDay[day] = dayData?.slots || [];
    });

    const maxSlots = Math.max(...weekDays.map((day) => slotsByDay[day].length));

    const rows = Array.from({ length: maxSlots }, (_, i) => {
      const row = {};
      weekDays.forEach((day) => {
        const slot = slotsByDay[day][i];
        row[day] = slot ? `${slot.start} - ${slot.end}` : "";
      });
      return row;
    });

    return rows;
  };

  const exportSchedule = () => {
    const header = [
      "Station Name",
      ...weekDays.map((day) => day.charAt(0).toUpperCase() + day.slice(1)),
    ];
    const csvRows = [header.join(",")];

    filteredGroupedScheduleItems.forEach((item) => {
      if (item.isMulti) {
        item.substations.forEach((sub) => {
          const days = sub.schedule?.days ?? [];
          const normalized = normalizeSlots(days);
          const label = `"${item.label} | ${sub.name}"`;
          if (normalized.length === 0) {
            csvRows.push([label, ...weekDays.map(() => '""')].join(","));
          } else {
            normalized.forEach((row) => {
              csvRows.push(
                [label, ...weekDays.map((day) => `"${row[day] || ""}"`)].join(
                  ",",
                ),
              );
            });
          }
        });
      } else if (item.schedule) {
        const normalized = normalizeSlots(item.schedule.days);
        normalized.forEach((row) => {
          const rowValues = [
            `"${item.label}"`,
            ...weekDays.map((day) => `"${row[day] || ""}"`),
          ];
          csvRows.push(rowValues.join(","));
        });
      }
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "All_Stations_Schedule.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const WeekdaySelector = ({ selectedDate, onChange }) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const startOfWeek = startOfWeekFn(selectedDate);

    return (
      <div className={styles.weekdaySwitcher}>
        {days.map((day, i) => {
          const date = addDays(startOfWeek, i);
          const isSelected = isSameDay(date, selectedDate);

          return (
            <button
              key={day}
              onClick={() => onChange(date)}
              className={`${styles.weekdayBtn} ${
                isSelected ? `${styles.selected}` : ""
              }`}
            >
              <span>{day.toUpperCase()}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageHeader}>
        <h2>Schedule</h2>
        {isGetStationsSuccess && (
          <div className={styles.selectorWrapper}>
            <Dropdown
              value={null}
              onChange={(e) => {
                setScheduleFilter(e.value);
                setShowTableModal(true);
              }}
              options={scheduleFilterOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Station Schedules"
              className={styles.scheduleFilterDropdown}
              panelClassName={styles.scheduleFilterDropdownPanel}
            />
            <Dropdown
              value={selectedStation}
              onChange={(e) => changeStation(e.value)}
              options={stationDropdownOptions}
              optionLabel="name"
              optionDisabled={(option) => option._isGroupHeader}
              placeholder="Select your station"
              className={`${styles.dropdown}`}
              panelClassName={styles.scheduleDropdownPanel}
              itemTemplate={(option) => {
                if (option._isGroupHeader) {
                  return (
                    <div className={styles.dropdownGroupHeader}>
                      {option._subCount > 0 && (
                        <span
                          className={styles.dropdownGroupBadge}
                          title={`${option._subCount} sub-stations`}
                        >
                          x{option._subCount}
                        </span>
                      )}
                      {option.name}
                    </div>
                  );
                }
                if (option._isSubstation) {
                  return (
                    <div className={styles.dropdownSubItem}>{option.name}</div>
                  );
                }
                return <div>{option.name}</div>;
              }}
              valueTemplate={(option) => {
                if (!option) return <span>Select your station</span>;
                if (option._isSubstation) {
                  return (
                    <span>
                      {option._parentName} › {option.name}
                    </span>
                  );
                }
                return <span>{option.name}</span>;
              }}
            />
          </div>
        )}
      </div>
      {isMobile && selectedStation && (
        <WeekdaySelector
          selectedDate={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            calendarRef.current.getApi().gotoDate(date);
          }}
        />
      )}
      <div className={styles.actions}>
        {selectedStation && (
          <div className={styles.createSlot}>
            {isMobile && <span>Long Press + drag to create a slot</span>}
            <div className={styles.dragCreateBtn} id="mydraggable">
              <i className="pi pi-plus" />
              <span>{" DRAG TO ADD SLOT"}</span>
            </div>
          </div>
        )}
        {isDragging && (
          <div id="delete_event" className={styles.deleteSlot}>
            <i className="pi pi-trash mr-3" />
            <span>DROP HERE TO DELETE</span>
          </div>
        )}

        {isDirty && (
          <div className={styles.saveBtnWrapper}>
            <Button className={styles.saveBtn} onClick={saveSchedule}>
              <span>SAVE SCHEDULE</span>
              <i className="pi pi-check-square"></i>
            </Button>
          </div>
        )}
      </div>

      {isGetSchedulingSuccess && (
        <>
          {selectedStation ? (
            <div className={styles.calendarWrapper}>
              <FullCalendar
                key={isMobile ? "mobile" : "desktop"}
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView={isMobile ? "timeGridDay" : "timeGridWeek"}
                dayHeaderFormat={isMobile ? false : { weekday: "long" }}
                slotDuration="00:15:00"
                slotMinTime="00:00:00"
                slotMaxTime="23:59:00"
                allDaySlot={false}
                headerToolbar={false}
                slotLabelInterval="01:00:00"
                scrollTime={"06:00:00"}
                eventTimeFormat={{
                  hour: "2-digit",
                  minute: "2-digit",
                  meridiem: true,
                }}
                eventOverlap={false}
                events={Object.values(slots).reduce(
                  (prev, curr) => [...prev, ...curr],
                  [],
                )}
                eventColor={isMobile ? "#58A4A4" : "#94BCE6"}
                locale=""
                height={700}
                droppable={true}
                eventShortHeight={60}
                drop={(e) => {
                  console.log("dropped", e);
                  const startTime = format(e.date, "HH:mm");
                  const endTime = format(addHours(e.date, 1), "HH:mm");

                  const event = {
                    title: "",
                    daysOfWeek: [],
                    startTime: startTime,
                    endTime: endTime,
                    constraint: { daysOfWeek: [] },
                  };

                  const eventDay = format(e.date, "EEEE").toLowerCase();

                  if (slots[eventDay].length === 10) {
                    return;
                  }

                  let isOverlapping = false;

                  slots[eventDay].forEach((slot) => {
                    if (
                      isAfter(
                        parse(startTime, "HH:mm", new Date()),
                        parse(slot.startTime, "HH:mm", new Date()),
                      ) &&
                      isBefore(
                        parse(endTime, "HH:mm", new Date()),
                        parse(slot.endTime, "HH:mm", new Date()),
                      )
                    ) {
                      isOverlapping = true;
                      return;
                    }
                  });

                  if (isOverlapping) {
                    return;
                  }

                  switch (eventDay) {
                    case "sunday":
                      event.daysOfWeek.push(0);
                      event.constraint.daysOfWeek.push(0);
                      break;
                    case "monday":
                      event.daysOfWeek.push(1);
                      event.constraint.daysOfWeek.push(1);
                      break;
                    case "tuesday":
                      event.daysOfWeek.push(2);
                      event.constraint.daysOfWeek.push(2);
                      break;
                    case "wednesday":
                      event.daysOfWeek.push(3);
                      event.constraint.daysOfWeek.push(3);
                      break;
                    case "thursday":
                      event.daysOfWeek.push(4);
                      event.constraint.daysOfWeek.push(4);
                      break;
                    case "friday":
                      event.daysOfWeek.push(5);
                      event.constraint.daysOfWeek.push(5);
                      break;
                    case "saturday":
                      event.daysOfWeek.push(6);
                      event.constraint.daysOfWeek.push(6);
                      break;
                  }

                  const newSameDayEvents = [...slots[eventDay], event]
                    .sort((a, b) =>
                      compareAsc(
                        parse(a.startTime, "HH:mm", new Date()),
                        parse(b.startTime, "HH:mm", new Date()),
                      ),
                    )
                    .map((slot, index) => ({
                      ...slot,
                      title: `Slot ${index + 1}`,
                    }));

                  setSlots((prevSlots) => ({
                    ...prevSlots,
                    [eventDay]: newSameDayEvents,
                  }));
                  setIsDirty(true);
                  dispatch(setUnsavedChanges(true));
                }}
                eventDragStart={(info) => {
                  const el = info.el;
                  draggingElRef.current = el;
                  el.classList.add("drag-resize");
                  setIsDragging(true);
                }}
                eventDragStop={(info) => {
                  const deleteZone = document.getElementById("delete_event");
                  if (!deleteZone) return;

                  const deleteZoneRect = deleteZone.getBoundingClientRect();

                  let pageX, pageY;

                  if (info.jsEvent.type.startsWith("touch")) {
                    const touch = info.jsEvent.changedTouches?.[0];
                    if (touch) {
                      pageX = touch.pageX;
                      pageY = touch.pageY;
                    }
                  } else {
                    pageX = info.jsEvent.pageX;
                    pageY = info.jsEvent.pageY;
                  }

                  if (pageX == null || pageY == null) return;

                  const isInDeleteZone =
                    pageX >= deleteZoneRect.left + window.scrollX &&
                    pageX <= deleteZoneRect.right + window.scrollX &&
                    pageY >= deleteZoneRect.top + window.scrollY &&
                    pageY <= deleteZoneRect.bottom + window.scrollY;

                  if (isInDeleteZone) {
                    const eventDayName = format(
                      info.event.start,
                      "EEEE",
                    ).toLowerCase();

                    setSlots((prev) => ({
                      ...prev,
                      [eventDayName]: [
                        ...slots[eventDayName]
                          .filter((slot) => slot.title !== info.event.title)
                          .map((item, index) => ({
                            ...item,
                            title: `Slot ${index + 1}`,
                          })),
                      ],
                    }));

                    setIsDirty(true);
                    dispatch(setUnsavedChanges(true));
                  }

                  const el = info.el;
                  el.classList.remove("drag-resize");
                  draggingElRef.current = null;
                  setIsDragging(false);
                }}
                eventChange={(info) => {
                  const newStart = format(info.event.start, "HH:mm");
                  let newEnd = format(info.event.end, "HH:mm");

                  if (newEnd === "00:00") {
                    newEnd = "23:59";
                  }

                  // Prevent zero-length or reversed slot
                  if (
                    newStart === newEnd ||
                    isAfter(
                      parse(newStart, "HH:mm", new Date()),
                      parse(newEnd, "HH:mm", new Date()),
                    )
                  ) {
                    info.revert();
                    return;
                  }

                  const eventDayName = format(
                    info.event.start,
                    "EEEE",
                  ).toLowerCase();

                  const sameDayEvents = slots[eventDayName]
                    .map((slot) => {
                      if (
                        slot.startTime === format(info.oldEvent.start, "HH:mm")
                      ) {
                        return {
                          ...slot,
                          startTime: newStart,
                          endTime: newEnd,
                        };
                      }
                      return slot;
                    })
                    .toSorted((a, b) =>
                      compareAsc(
                        parse(a.startTime, "HH:mm", new Date()),
                        parse(b.startTime, "HH:mm", new Date()),
                      ),
                    )
                    .map((slot, index) => {
                      return { ...slot, title: `Slot ${index + 1}` };
                    });

                  console.log(newStart, newEnd, eventDayName, sameDayEvents);

                  setSlots((prev) => ({
                    ...prev,
                    [eventDayName]: sameDayEvents,
                  }));
                  setIsDirty(true);
                  dispatch(setUnsavedChanges(true));
                }}
                // Interactive plugin configurations
                editable={true}
                eventStartEditable={true}
                eventDurationEditable={true}
                eventResizableFromStart={true} // Allow resizing from start and end
                snapDuration="00:15:00" // Snap to 15-minute intervals when resizing
              />
            </div>
          ) : (
            <div>Select station please</div>
          )}
        </>
      )}

      <Dialog
        closable={false}
        header={
          <div style={{ width: "100%", textAlign: "center" }}>Leave page?</div>
        }
        visible={showAlertDialog}
        onHide={() => {
          setShowAlertDialog(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Leave"
              className={`p-button-success ${styles.confirmDialogBtn}`}
              onClick={confirmChangeStation}
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowAlertDialog(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.alertModal}`}
      >
        <div className={styles.text}>
          <span>Changes you made may not be saved.</span>
        </div>
      </Dialog>

      <Dialog
        closable={true}
        header={
          <div className={styles.modalHeaderWrapper}>
            <h4>Organization's Schedule</h4>
            <div className={styles.modalHeaderBts}>
              <Button
                label="Print"
                icon="pi pi-print"
                className={styles.scheduleTableBtn}
                onClick={reactToPrintFn}
              />
              <Button
                label="Export"
                icon="pi pi-file-export"
                className={styles.scheduleTableBtn}
                onClick={exportSchedule}
              />
            </div>
          </div>
        }
        visible={showTableModal}
        onHide={() => {
          setShowTableModal(false);
        }}
        className={`p-fluid ${styles.tableModal}`}
      >
        {isGetSchedulingSuccess && isGetStationsSuccess && (
          <div className={styles.scheduleTablesWrapper} ref={contentRef}>
            {filteredGroupedScheduleItems.map((item) => {
              if (item.isMulti) {
                return (
                  <div key={item.id}>
                    <div className={styles.multiStationSectionLabel}>
                      {item.subCount > 0 && (
                        <span className={styles.dropdownGroupBadge}>
                          x{item.subCount}
                        </span>
                      )}
                      {item.label}
                    </div>
                    {item.substations.map((sub) => (
                      <div
                        key={sub.id}
                        className={`${styles.scheduleTable} ${styles.scheduleTableSub}`}
                      >
                        <h3>{sub.name}</h3>
                        <DataTable
                          value={normalizeSlots(sub.schedule?.days ?? [])}
                        >
                          {weekDays.map((day) => (
                            <Column
                              key={day}
                              field={day}
                              header={
                                day.charAt(0).toUpperCase() + day.slice(1)
                              }
                            />
                          ))}
                        </DataTable>
                      </div>
                    ))}
                  </div>
                );
              }
              return item.schedule ? (
                <div key={item.id} className={styles.scheduleTable}>
                  <h3>{item.label}</h3>
                  <DataTable value={normalizeSlots(item.schedule.days)}>
                    {weekDays.map((day) => (
                      <Column
                        key={day}
                        field={day}
                        header={day.charAt(0).toUpperCase() + day.slice(1)}
                      />
                    ))}
                  </DataTable>
                </div>
              ) : null;
            })}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Scheduling;
